import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, ShoppingBag, Users, DollarSign, LogOut } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchStats();
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  if (!user || user.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Admin Header */}
      <header className="bg-primary text-white border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="font-heading text-2xl font-bold uppercase" data-testid="admin-dashboard-heading">Admin Dashboard</h1>
          <div className="flex items-center gap-4">
            <span className="text-sm">{user.name}</span>
            <Button onClick={handleLogout} variant="outline" size="sm" className="border-white text-white hover:bg-white hover:text-primary" data-testid="admin-logout">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Admin Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 py-4">
            <Link to="/admin" className="text-sm font-medium text-secondary border-b-2 border-secondary pb-1">
              Dashboard
            </Link>
            <Link to="/admin/products" className="text-sm font-medium text-slate-600 hover:text-secondary">
              Products
            </Link>
            <Link to="/admin/categories" className="text-sm font-medium text-slate-600 hover:text-secondary">
              Categories
            </Link>
            <Link to="/admin/orders" className="text-sm font-medium text-slate-600 hover:text-secondary">
              Orders
            </Link>
            <Link to="/admin/chats" className="text-sm font-medium text-slate-600 hover:text-secondary">
              Chats
            </Link>
            <Link to="/admin/shop-info" className="text-sm font-medium text-slate-600 hover:text-secondary">
              Shop Info
            </Link>
          </div>
        </div>
      </nav>

      {/* Dashboard Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Loading stats...</p>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white border-l-4 border-l-secondary shadow-sm p-6 rounded-r-sm" data-testid="stat-products">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1 uppercase">Total Products</p>
                    <p className="text-3xl font-bold text-primary">{stats?.total_products || 0}</p>
                  </div>
                  <Package className="h-12 w-12 text-secondary/20" />
                </div>
              </div>

              <div className="bg-white border-l-4 border-l-accent shadow-sm p-6 rounded-r-sm" data-testid="stat-orders">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1 uppercase">Total Orders</p>
                    <p className="text-3xl font-bold text-primary">{stats?.total_orders || 0}</p>
                  </div>
                  <ShoppingBag className="h-12 w-12 text-accent/20" />
                </div>
              </div>

              <div className="bg-white border-l-4 border-l-green-500 shadow-sm p-6 rounded-r-sm" data-testid="stat-customers">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1 uppercase">Total Customers</p>
                    <p className="text-3xl font-bold text-primary">{stats?.total_users || 0}</p>
                  </div>
                  <Users className="h-12 w-12 text-green-500/20" />
                </div>
              </div>

              <div className="bg-white border-l-4 border-l-yellow-500 shadow-sm p-6 rounded-r-sm" data-testid="stat-revenue">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1 uppercase">Total Revenue</p>
                    <p className="text-3xl font-bold text-primary">₹{stats?.total_revenue?.toFixed(2) || '0.00'}</p>
                  </div>
                  <DollarSign className="h-12 w-12 text-yellow-500/20" />
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-sm shadow-sm p-6">
              <h2 className="font-heading text-2xl font-bold text-primary mb-6 uppercase">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Link to="/admin/products">
                  <Button className="w-full" data-testid="manage-products-link">
                    <Package className="h-5 w-5 mr-2" />
                    Manage Products
                  </Button>
                </Link>
                <Link to="/admin/orders">
                  <Button className="w-full" variant="outline" data-testid="manage-orders-link">
                    <ShoppingBag className="h-5 w-5 mr-2" />
                    Manage Orders
                  </Button>
                </Link>
                <Link to="/admin/categories">
                  <Button className="w-full" variant="outline" data-testid="manage-categories-link">
                    <Package className="h-5 w-5 mr-2" />
                    Manage Categories
                  </Button>
                </Link>
                <Link to="/admin/shop-info">
                  <Button className="w-full" variant="outline" data-testid="manage-shop-info-link">
                    <Package className="h-5 w-5 mr-2" />
                    Shop Info
                  </Button>
                </Link>
              </div>
            </div>

            {stats?.pending_orders > 0 && (
              <div className="bg-yellow-50 border-l-4 border-l-yellow-500 rounded-sm p-6 mt-6">
                <p className="text-yellow-800">
                  <span className="font-bold">{stats.pending_orders}</span> pending orders require attention.
                  <Link to="/admin/orders" className="ml-2 underline">View Orders</Link>
                </p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;